import { Component, computed, effect, inject, linkedSignal, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { Contact } from '../../../api/app-types/contact.app';
import { CONTACT_API } from '../../../api/services/contact.service';
import { LvrHeading } from '../../../core/components/heading/heading';
import { Paginator } from '../../../core/components/paginator/paginator';
import { LvrSimplePanel } from '../../../core/components/simple-panel/simple-panel';
import { REPRESENTATION_MODE } from '../../../core/services/representation-mode.service';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification } from '../../../shared/notification/notification.factory';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { ContactCard } from '../../contact/contact-card/contact-card';

@Component({
	selector: 'lvr-network-contact-overview',
	imports: [TranslocoPipe, LvrHeading, MatIcon, ContactCard, LvrSimplePanel, Paginator, RouterLink],
	templateUrl: './network-contact-overview.html',
	styleUrl: './network-contact-overview.scss',
})
export class NetworkContactOverview extends RouteInjection {
	private readonly env = inject(ENVIRONMENT);
	private readonly api = inject(CONTACT_API);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly representationMode = inject(REPRESENTATION_MODE);

	private readonly lastRepresentationKey = signal<string | null>(null);
	private readonly representationKey = computed(() => {
		const isActive = this.representationMode.isActive();
		return isActive ? (this.representationMode.mode().representedUserId ?? '') : '';
	});

	private readonly page = this.resolveDataAsSignal<PaginatedResponse<Contact>>('contacts');
	private readonly initialPage = computed<PaginatedResponse<Contact>>(() => {
		const pagination = this.page();
		return {
			entities: pagination?.entities ?? [],
			offset: pagination?.offset ?? this.env.pagination.range,
			range: pagination?.range ?? this.env.pagination.range,
			totalEntities: pagination?.totalEntities ?? 0,
		};
	});
	public readonly linkedPage = linkedSignal({
		source: this.initialPage,
		computation: (init) => ({
			...init,
			range: init.range || this.env.pagination.range,
		}),
	});

	public readonly loading = signal(false);
	public readonly showFirstLastButtons = true;
	public readonly hidePageSize = false;

	public readonly contacts = computed(() => this.linkedPage().entities);
	public readonly totalItems = computed(() => this.linkedPage().totalEntities);
	public readonly pageIndex = computed(() => this.page()?.offset ?? 0);
	public readonly pageSize = computed(() => this.page()?.range ?? this.env.defaultPageSize);

	public constructor() {
		super();
		effect(() => {
			const key = this.representationKey();

			if (this.lastRepresentationKey() === null) {
				this.lastRepresentationKey.set(key);

				if (key) {
					void this.reloadPage(0, this.env.pagination.range);
				}
				return;
			}

			if (this.lastRepresentationKey() === key) return;

			this.lastRepresentationKey.set(key);
			void this.reloadPage(0, this.env.pagination.range);
		});
	}

	public handlePageEvent(event: PageEvent) {
		this.reloadPage(event.pageIndex, event.pageSize);
	}

	private async reloadPage(pageIndex: number, pageSize: number) {
		this.loading.set(true);
		try {
			const userId = this.representationMode.isActive() ? this.representationMode.mode().representedUserId : undefined;
			const update = await this.api.getDashboardContacts(pageIndex, pageSize, userId);
			this.linkedPage.set({ ...update, offset: pageIndex, range: pageSize });
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.network_contacts_reloading_error')),
			);
		} finally {
			this.loading.set(false);
		}
	}
}
